import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import {Column, ColumnCellProps, Table, TableProps} from 'fixed-data-table-2';
import reactUpdate from 'immutability-helper';
import React, {useCallback, useMemo} from 'react';
import _ from 'underscore';

import {assert, assertAndContinue} from 'Asserts';
import AutoSizer from 'components/AutoSizer';
import DataConnector from 'connect/DataConnector';
import ColumnFilterDropdown, {getVisibleColumns} from 'nav/expanded/ColumnFilterDropdown';
import preferences from 'Preferences';
import getTestProps from 'test/lib/cypress/getTestProps';
import IndeterminateCheckbox from 'ui-library/IndeterminateCheckbox';
import {getRowHeightBuffer} from 'ui-library/scrollable/ScrollableTableUtil';
import preserveShallowEquality from 'util/preserveShallowEquality';
import {assertNever, BasicEvent, Optional} from 'util/Types';

import EmptyScrollableTable from './EmptyScrollableTable';
import isRowIndexOutOfBounds from './isRowIndexOutOfBounds';
import {ScrollableTableUISelector} from './reduceScrollableTableUI';
import ScrollableTableCell from './ScrollableTableCell';
import {
  CreateCustomSortElement,
  RowSelectionMode,
  ScrollableTableColumn,
  ScrollableTablePreferences,
  ScrollableTableRow,
  TABLE_HEADER_HEIGHT,
  TABLE_ROW_BODY_HEIGHT,
  VERTICAL_SCROLLBAR_WIDTH,
} from './ScrollableTableConstants';
import ScrollableTableHeaderCell from './ScrollableTableHeaderCell';
import ScrollableTableHoverCell from './ScrollableTableHoverCell';
import * as ScrollableTableUIReduxActions from './ScrollableTableUIReduxActions';

const TABLE_DEFAULT_LEFT_PADDING = 10;
export const DEFAULT_TABLE_COLUMN_WIDTH = 165;

const enum TableType {
  FIXED_ROWS_TOP = 'FIXED_ROWS_TOP',
  FIXED_ROWS_BOTTOM = 'FIXED_ROWS_BOTTOM',
  NO_FIXED_ROWS = 'NO_FIXED_ROWS',
}

export interface BaseScrollableTableProps {
  allowOverflow?: boolean;
  calcLeftPadding?: (width: number) => number;
  rowSelectionMode?: RowSelectionMode | null;
  disableSelection?: boolean;
  className?: string;
  columns: Array<ScrollableTableColumn>;
  emptyTableText?: string;
  getRowContentHeight?: (rowIndex: number) => number;
  matchParentHeight?: boolean;
  firstHeaderCellNode?: React.ReactNode;
  hasLineBelowHeader?: boolean;
  headerClassName?: string;
  isCompact?: boolean;
  onChangeSort?: (sortKey: string) => void;
  createCustomSortElementByColumn?: {[columnKey: string]: CreateCustomSortElement};
  onRowClick?: (row: ScrollableTableRow, e?: React.SyntheticEvent<Table>) => void;
  onRowContextMenu?: (row: ScrollableTableRow, e: BasicEvent) => void;
  onRowToggleExpanded?: (row: ScrollableTableRow, isExpanded: boolean) => void;
  onRowMouseEnter?: (row: ScrollableTableRow) => void;
  onRowMouseLeave?: (row: ScrollableTableRow) => void;
  onRowsSelect?: (rows: Array<ScrollableTableRow>, isSelected: boolean) => void;
  rows: Array<ScrollableTableRow>;
  scrollToRow?: number;
  defaultHiddenColumnKeys?: Array<string> | null;
  canResizeColumns?: boolean;
  canFilterColumns?: boolean;
  onFilterColumns?: (hiddenColumnKeys: Array<string>) => void;
  tableKey?: string;
  alwaysShowSelector?: boolean;
  scrollLeft?: number;
  /**
   * This prop is only used when there are fixed rows. By default, the fixed top and bottom
   * tables are synced on scrolling. This behavior can be overridden by providing a custom
   * onHorizontalScroll callback. The callback should return false to stop propagation.
   * See doc here for details:
   * https://schrodinger.github.io/fixed-data-table-2/api-table.html#onHorizontalScroll.
   */
  onHorizontalScroll?: (scrollX: number) => boolean;
  onColumnResizeEndCallback?: (newColWidth: number, columnKey: string) => void;
  bufferRowCount?: number;
  /** Number of fixed rows. Only behaves expectedly when there is no horizontal scrollbar */
  fixedRowCount?: number;
  onResize?: (size: {height: number; width: number}) => void;
  onScrollEnd?: (x: number, y: number) => void;
  /**
   * Reference to the underlying FixedDataTable component.
   * For tables with fixed rows, it references the top table.
   */
  tableRef?: React.RefObject<Table>;
}

export interface EnhancedScrollableTableProps {
  hiddenColumnKeys: Array<string>;
  onUpdateHiddenColumns?: (hiddenColumnKeys: Array<string>) => void;
}

interface State {
  anchorRowId: Optional<string>;
  collapsedColumns: Array<string>;
  colWidthPreferences: {[k: string]: number};
  // This is used to sync the horizontal scroll for the fixed top and bottom tables.
  syncedScrollLeft: number;
}

// Props if not canFilterColumns.
const DISABLED_PROPS: EnhancedScrollableTableProps = {
  hiddenColumnKeys: [],
  onUpdateHiddenColumns: undefined,
};

const defaultProps = {
  allowOverflow: true,
  canFilterColumns: false,
  canResizeColumns: false,
  emptyTableText: 'No rows',
  getRowContentHeight: (_: number) => TABLE_ROW_BODY_HEIGHT,
  hasLineBelowHeader: true,
  isCompact: false,
  matchParentHeight: false,
  fixedRowCount: 0,
};

/**
 * Connector for ScrollableTable to manage hidden columns if the table allows filtering columns.
 */
class ScrollableTableConnector extends DataConnector<BaseScrollableTableProps, EnhancedScrollableTableProps> {
  enhanceProps: () => EnhancedScrollableTableProps;

  constructor(props: BaseScrollableTableProps) {
    super(props);
    this.enhanceProps = preserveShallowEquality(this.uncachedEnhanceProps);
  }

  private handleUpdateHiddenColumns = (hiddenColumnKeys: Array<string>): void => {
    this.props.onFilterColumns?.(hiddenColumnKeys);
    this.dispatch(
      ScrollableTableUIReduxActions.updateHiddenColumns(this.props.tableKey ?? '', hiddenColumnKeys)
    );
  };

  uncachedEnhanceProps = (): EnhancedScrollableTableProps => {
    if (!this.props.canFilterColumns) {
      return DISABLED_PROPS;
    }
    assertAndContinue(this.props.tableKey != null, 'Expected tableKey if canFilterColumns');
    const selector = new ScrollableTableUISelector(this.reduxState());

    return {
      hiddenColumnKeys: selector.getHiddenColumnKeysForTable(
        this.props.tableKey ?? '',
        this.props.defaultHiddenColumnKeys
      ),
      onUpdateHiddenColumns: this.handleUpdateHiddenColumns,
    };
  };
}

export type ScrollableTableProps = BaseScrollableTableProps &
  EnhancedScrollableTableProps &
  typeof defaultProps;

export class ScrollableTable extends React.PureComponent<ScrollableTableProps, State> {
  static defaultProps = defaultProps;

  constructor(props: BaseScrollableTableProps & EnhancedScrollableTableProps & typeof defaultProps) {
    super(props);
    this.state = this.getInitialState();
  }

  getInitialState = (): State => {
    // Store column width preferences so the component will immediately update with the new preference.
    const colWidthPreferences: State['colWidthPreferences'] = {};
    if (this.props.canResizeColumns) {
      this.props.columns.forEach((column) => {
        assert(this.props.tableKey != null, 'tableKey is required if canResizeColumns');
        const columnKey = column.columnKey || column.accessorKey;
        const preferredWidth = preferences.get(
          ScrollableTable.getColumnWidthPreferenceKey(this.props.tableKey, columnKey)
        );
        if (preferredWidth !== undefined) {
          colWidthPreferences[columnKey] = preferredWidth;
        }
      });
    }
    return {
      anchorRowId: null, // the first selected row from which to start the selection range for shift-select
      // List of collapsed column accessorKeys
      collapsedColumns: this.props.columns
        .filter((column) =>
          preferences.get(`${ScrollableTablePreferences.COLLAPSED_NAMESPACE}${column.accessorKey}`)
        )
        .map((column) => column.accessorKey),
      colWidthPreferences,
      syncedScrollLeft: 0,
    };
  };

  /**
   * Sync the horizontal scroll fixed top and bottom tables. Reference:
   * https://github.com/schrodinger/fixed-data-table-2/blob/522e82be907c41c250b231a76e3127abed9aed81/examples/FixedRowsExample.js
   */
  private handleSyncedHorizontalScroll = (scrollX: number): boolean => {
    this.setState({syncedScrollLeft: scrollX});
    return false;
  };

  public static getColumnWidthPreferenceKey = (tableKey: string, columnKey: string): string =>
    `${tableKey}.column_width.${columnKey}`;

  private isRowSelected = (row: ScrollableTableRow): boolean => row.isSelected ?? false;

  private handleShiftSelect = (id: string): void => {
    // eslint-disable-next-line no-restricted-syntax
    const ids = _.pluck(this.props.rows, 'id');

    // Region from anchor to clickIndex gets selected/deselected
    const anchorIndex = this.state.anchorRowId ? ids.indexOf(this.state.anchorRowId) : 0;
    const clickIndex = ids.indexOf(id);
    const rowsToSelect = this.props.rows.slice(
      Math.min(anchorIndex, clickIndex),
      Math.max(anchorIndex, clickIndex) + 1
    );
    assert(this.props.onRowsSelect != null, 'Expected non-null onRowsSelect prop');
    this.props.onRowsSelect(rowsToSelect, !this.props.rows[clickIndex].isSelected);
  };

  private handleRowSelect = (row: ScrollableTableRow, isSelected: boolean, isShift: boolean): void => {
    assert(this.props.onRowsSelect != null, 'Expected non-null onRowsSelect prop');
    if (this.props.rowSelectionMode === RowSelectionMode.SINGLE && isSelected) {
      this.props.onRowsSelect(
        this.props.rows.filter((propRow) => propRow.id !== row.id),
        false
      );
      this.props.onRowsSelect([row], true);
    } else if (isShift && this.props.rowSelectionMode !== RowSelectionMode.SINGLE) {
      this.handleShiftSelect(row.id);
    } else {
      this.props.onRowsSelect([row], isSelected);
    }
    this.setState({anchorRowId: row.id});
  };

  private handleGlobalSelect = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const isSelected = e.target.checked;
    assert(this.props.onRowsSelect != null, 'Expected non-null onRowsSelect prop');
    const selectableRows = this.props.rows.filter((row) => row.isSelectorPresent ?? true);
    this.props.onRowsSelect(selectableRows, isSelected);
  };

  private handleToggleCollapse = (column: ScrollableTableColumn): void => {
    const collapsedIndex = this.state.collapsedColumns.indexOf(column.accessorKey);
    const isCollapsed = collapsedIndex !== -1;
    let newCollapsedColumns;
    if (isCollapsed) {
      newCollapsedColumns = reactUpdate(this.state.collapsedColumns, {$splice: [[collapsedIndex, 1]]});
    } else {
      newCollapsedColumns = reactUpdate(this.state.collapsedColumns, {$push: [column.accessorKey]});
    }
    this.setState({collapsedColumns: newCollapsedColumns});
    preferences.set(`${ScrollableTablePreferences.COLLAPSED_NAMESPACE}${column.accessorKey}`, !isCollapsed);
  };

  private getRowClassName = (rowIndex: number): string => {
    if (isRowIndexOutOfBounds(this.props.rows, rowIndex)) {
      return 'scrollableTable-row';
    }
    const row = this.props.rows[rowIndex];
    return classNames('scrollableTable-row', row.className, row.rowOnlyClassName, {
      'scrollableTable-row--isSelected': row.isSelected,
      'scrollableTable-row--selectable': Boolean(this.props.onRowClick),
      'scrollableTable-row--faded': row.isFaded,
    });
  };

  private getRowHeight = (rowIndex: number): number => {
    if (isRowIndexOutOfBounds(this.props.rows, rowIndex)) {
      rowIndex = 0; // This is a safe default because getRowContentHeight must always handle rowIndex=0
    }
    const TABLE_ROW_HEIGHT_BUFFER = getRowHeightBuffer(this.props.isCompact);
    return this.props.getRowContentHeight(rowIndex) + TABLE_ROW_HEIGHT_BUFFER;
  };

  private getSumRowHeight = (startRowIndex: number, endRowIndex: number): number => {
    let sum = 0;
    for (let i = startRowIndex; i < endRowIndex; i++) {
      sum += this.getRowHeight(i);
    }
    return sum;
  };

  private getColWidth = (column: ScrollableTableColumn, tableWidth: number): number => {
    const TABLE_FIXED_COLUMN_MIN_WIDTH = this.props.isCompact ? 120 : 320; // min width of the fixed column
    // The fraction of the screen width that the fixed column should take up
    // (assuming it's larger than the min width)
    const TABLE_FIXED_COLUMN_WIDTH_FRACTION = this.props.isCompact ? 0.15 : 0.3125;
    const TABLE_COLLAPSED_COLUMN_WIDTH = 66;

    if (this.isCollapsed(column)) {
      return TABLE_COLLAPSED_COLUMN_WIDTH;
    } else if (column.width) {
      return column.width;
    } else if (column.isFixed) {
      return Math.max(TABLE_FIXED_COLUMN_WIDTH_FRACTION * tableWidth, TABLE_FIXED_COLUMN_MIN_WIDTH);
    } else {
      return DEFAULT_TABLE_COLUMN_WIDTH;
    }
  };

  private handleRowClick = (e: React.SyntheticEvent<Table>, rowIndex: number): void => {
    if (this.props.rows[rowIndex].isExpandable) {
      this.props.onRowToggleExpanded!(this.props.rows[rowIndex], !this.props.rows[rowIndex].isExpanded);
    } else if (this.props.onRowClick) {
      this.props.onRowClick(this.props.rows[rowIndex], e);
    }
  };

  private handleRowMouseEnter = (e: React.SyntheticEvent<Table>, rowIndex: number): void => {
    if (this.props.onRowMouseEnter) {
      this.props.onRowMouseEnter(this.props.rows[rowIndex]);
    }
  };

  private handleRowMouseLeave = (e: React.SyntheticEvent<Table>, rowIndex: number): void => {
    if (this.props.onRowMouseLeave) {
      this.props.onRowMouseLeave(this.props.rows[rowIndex]);
    }
  };

  private handleColumnResizeEndCallback = (newColWidth: number, columnKey: string): void => {
    assert(this.props.tableKey != null, 'tableKey is required if canResizeColumns');
    this.setState({colWidthPreferences: {...this.state.colWidthPreferences, [columnKey]: newColWidth}});
    preferences.set(ScrollableTable.getColumnWidthPreferenceKey(this.props.tableKey, columnKey), newColWidth);
    this.props.onColumnResizeEndCallback?.(newColWidth, columnKey);
  };

  private isCollapsed = (column: ScrollableTableColumn): boolean =>
    this.state.collapsedColumns.indexOf(column.accessorKey) !== -1;

  private getAdjustedRows = (tableType: TableType): Array<ScrollableTableRow> => {
    if (tableType === TableType.FIXED_ROWS_TOP) {
      return this.props.rows.slice(0, this.props.fixedRowCount);
    } else if (tableType === TableType.FIXED_ROWS_BOTTOM) {
      return this.props.rows.slice(this.props.fixedRowCount, this.props.rows.length);
    } else if (tableType === TableType.NO_FIXED_ROWS) {
      return this.props.rows;
    } else {
      assertAndContinue(
        false,
        `Unexpected tableType: ${assertNever(tableType)}. Falling back to ${
          TableType.NO_FIXED_ROWS
        } behavior.`
      );
      return this.props.rows;
    }
  };

  private renderGlobalCheckbox = (): React.ReactNode => (
    <label className="scrollableTable-cell-iconWrapper">
      <IndeterminateCheckbox
        className="scrollableTable-cell-checkbox"
        items={this.props.rows}
        selectedMap={this.isRowSelected}
        onChange={this.handleGlobalSelect}
        disabled={this.props.disableSelection}
      />
    </label>
  );

  private renderColumn = (
    column: ScrollableTableColumn,
    isPrimary: boolean,
    noRightBorder: boolean,
    forceFlexGrow: boolean,
    colWidth: number,
    index: number,
    tableType: TableType
  ): JSX.Element => {
    // The columnKey is usually the same as the accessorKey, but sometimes we want to specify the columnKey
    // specially (if the accessorKey is too generalized, e.g. for tag columns).
    const columnKey = column.columnKey || column.accessorKey;
    const isColResizable = this.props.canResizeColumns && !column.isFixedRight;
    const colWidthPreference = this.state.colWidthPreferences[columnKey];
    // If there's a column width preference, remove flexGrow so that the preference is honored. Allow the
    // column with forceFlexGrow to flex to fill up remaining space.
    let flexGrow: number | undefined;
    if (forceFlexGrow) {
      flexGrow = 1;
    } else if (column.isFixed || (colWidthPreference && index < this.props.columns.length - 1)) {
      flexGrow = 0;
    } else {
      flexGrow = column.flexGrow;
    }
    const finalColWidth = isColResizable ? colWidthPreference || colWidth : colWidth;

    const rows = this.getAdjustedRows(tableType);

    return (
      <Column
        key={column.accessorKey}
        allowCellsRecycling={true}
        // eslint-disable-next-line react/no-unstable-nested-components -- Disabling for now, but worth fixing
        cell={({rowIndex}: ColumnCellProps) => (
          <ScrollableTableCell
            accessorKey={column.accessorKey}
            collapsedColumns={this.state.collapsedColumns}
            isSelectorPresent={
              this.props.rowSelectionMode != null && (rows[rowIndex].isSelectorPresent ?? true)
            }
            isSelectorDisabled={(this.props.disableSelection || rows[rowIndex].isSelectorDisabled) ?? false}
            selectorIconProps={rows[rowIndex].selectorIconProps}
            alwaysShowSelector={this.props.alwaysShowSelector}
            isFixed={column.isFixed ?? false}
            isPrimary={isPrimary}
            onContextMenu={this.props.onRowContextMenu}
            onSelect={this.handleRowSelect}
            rows={rows}
            dataTestAttrs={{component: 'ScrollableTable', element: 'cell', key: column.accessorKey}}
            rowIndex={rowIndex}
            numLinesToWrapText={
              // Uses getRowContentHeight(0) to get the height of an arbitrary row in the table, similarly to
              // how getRowHeight(0) is used in the render function for the table.
              // Note that calculation isn't guaranteed to work unless each row has the same, static height.
              column.wrapText
                ? Math.floor(this.props.getRowContentHeight(0) / TABLE_ROW_BODY_HEIGHT)
                : undefined
            }
            rowSelectionMode={this.props.rowSelectionMode}
            fillCell={column.fillCell}
          />
        )}
        columnKey={columnKey}
        fixed={column.isFixed}
        fixedRight={column.isFixedRight}
        flexGrow={flexGrow}
        header={
          tableType !== TableType.FIXED_ROWS_BOTTOM ? (
            <ScrollableTableHeaderCell
              checkbox={
                this.props.rowSelectionMode === RowSelectionMode.MULTIPLE && isPrimary
                  ? this.renderGlobalCheckbox()
                  : null
              }
              className={this.props.headerClassName}
              column={column}
              hasLineBelowHeader={this.props.hasLineBelowHeader}
              handleSort={this.props.onChangeSort}
              createCustomSortElement={this.props.createCustomSortElementByColumn?.[column.accessorKey]}
              handleToggleCollapse={this.handleToggleCollapse}
              headerCellNode={
                (isPrimary && this.props.firstHeaderCellNode) ||
                (isPrimary && this.props.canFilterColumns && (
                  <ColumnFilterDropdown
                    columns={this.props.columns.slice(1).filter((col) => !col.disableFiltering)}
                    hiddenColumnKeys={this.props.hiddenColumnKeys}
                    onUpdateHiddenColumns={this.props.onUpdateHiddenColumns ?? noop}
                  />
                ))
              }
              isCollapsed={this.isCollapsed(column)}
              isPrimary={isPrimary}
              noRightBorder={noRightBorder}
            />
          ) : undefined
        }
        isResizable={isColResizable}
        minWidth={100}
        width={finalColWidth}
      />
    );
  };

  private renderColumns(tableType: TableType, tableWidth: number): Array<JSX.Element> {
    const columns = getVisibleColumns(this.props.columns, this.props.hiddenColumnKeys);

    // In order for the table to render at full width we ensure that at least one column has flexGrow: 1.
    // Algorithm: pick the last column that does not have a system-specified width constraint. The table
    // will render awkwardly if no column is identified, so it is on the caller to ensure there is at least
    // one column can flex.
    const alreadyHasFlexGrow = columns.some((c) => !c.isFixed && !c.isFixedRight && c.flexGrow === 1);
    const flexGrowIdx = !alreadyHasFlexGrow
      ? (columns
          .map((column, i) => [column, i] as const)
          .filter(([c]) => !c.isFixed && !c.isFixedRight && c.width == null && c.flexGrow != null)
          .map(([, i]) => i)
          .pop() ?? null)
      : null;

    return columns.map((column, i, columns) => {
      const isPrimary = i === 0;
      const forceFlexGrow = flexGrowIdx === i;
      const colWidth = this.getColWidth(column, tableWidth);
      const isNextToFixedRightColumn = i < columns.length - 1 && (columns[i + 1].isFixedRight ?? false);

      // These cases should not have a right border:
      // - A fixed header cell already has a right border because fixed data table renders a border for
      //   the fixed column.
      // - A fixedRight header has a left border. So its left neighbor should not have a right border.
      const noRightBorder = column.isFixed || isNextToFixedRightColumn;

      return this.renderColumn(column, isPrimary, noRightBorder, forceFlexGrow, colWidth, i, tableType);
    });
  }

  private renderTable = (
    height: number,
    width: number,
    rowHeight: number,
    leftPadding: number,
    hasFixedColumn: boolean,
    headerHeight: number,
    tableType: TableType
  ): JSX.Element => {
    const header = (): JSX.Element => (
      <div
        className={classNames('scrollableTable-headerCell scrollableTable-headerCell--noContents', {
          'scrollableTable-headerCell--border': this.props.hasLineBelowHeader,
        })}
      />
    );

    const rows = this.getAdjustedRows(tableType);
    const rowCount = rows.length;

    const sumRowHeight = this.getSumRowHeight(this.props.fixedRowCount, rowCount + this.props.fixedRowCount);
    const hasVisibleFixedRightColumn = getVisibleColumns(
      this.props.columns,
      this.props.hiddenColumnKeys
    ).some((c) => c.isFixedRight);
    const displayRightPlaceholderColumn =
      hasVisibleFixedRightColumn &&
      (tableType === TableType.FIXED_ROWS_TOP ||
        (tableType === TableType.FIXED_ROWS_BOTTOM && sumRowHeight < height));

    // turn off horizontal scrollbar for fixed top and bottom tables
    const showScrollbarX = tableType === TableType.NO_FIXED_ROWS ? undefined : false;

    // add onHorizontalScroll for fixed top and bottom tables
    let onHorizontalScroll: ((scrollX: number) => boolean) | undefined;
    if (tableType !== TableType.NO_FIXED_ROWS) {
      onHorizontalScroll = this.props.onHorizontalScroll ?? this.handleSyncedHorizontalScroll;
    }

    // add table ref for the top table or non-fixed table
    const tableRef = tableType !== TableType.FIXED_ROWS_BOTTOM ? this.props.tableRef : undefined;

    return (
      <AdjustedTable
        tableRef={tableRef}
        tableType={tableType}
        fixedRowCount={this.props.fixedRowCount}
        getRowHeight={this.getRowHeight}
        getRowClassName={this.getRowClassName}
        headerHeight={headerHeight}
        height={height}
        isColumnResizing={false}
        onColumnResizeEndCallback={this.handleColumnResizeEndCallback}
        onRowClick={this.handleRowClick}
        onRowMouseEnter={this.handleRowMouseEnter}
        onRowMouseLeave={this.handleRowMouseLeave}
        rowsCount={rowCount}
        rowHeight={rowHeight}
        scrollToRow={this.props.scrollToRow}
        width={width}
        scrollLeft={this.props.scrollLeft ?? this.state.syncedScrollLeft}
        bufferRowCount={this.props.bufferRowCount}
        onScrollEnd={this.props.onScrollEnd}
        onHorizontalScroll={onHorizontalScroll}
        overflowX={undefined}
        showScrollbarX={showScrollbarX}
      >
        {
          // The first column has no contents. It exists to render a ScrollableTableHoverCell,
          // which creates a cell that covers the entire row whenever the row is covered. It also
          // provides left padding.
        }
        <Column
          // eslint-disable-next-line react/no-unstable-nested-components -- Disabling for now, but worth fixing
          cell={(fixedDataTableFrameworkProps) => (
            <ScrollableTableHoverCell
              {...fixedDataTableFrameworkProps}
              onContextMenu={this.props.onRowContextMenu}
              rowWidth={width}
              rows={rows}
            />
          )}
          fixed={hasFixedColumn}
          header={headerHeight > 0 ? header : undefined}
          width={leftPadding}
        />
        {this.renderColumns(tableType, width)}
        {displayRightPlaceholderColumn && (
          // Because of the absence of a vertical scrollbar, we need to add this to fill the space
          <Column
            // eslint-disable-next-line react/no-unstable-nested-components -- Disabling for now, but worth fixing
            cell={(fixedDataTableFrameworkProps) => (
              <ScrollableTableHoverCell
                {...fixedDataTableFrameworkProps}
                onContextMenu={this.props.onRowContextMenu}
                rowWidth={width}
                rows={rows}
              />
            )}
            fixedRight={true}
            header={headerHeight > 0 ? header : undefined}
            width={VERTICAL_SCROLLBAR_WIDTH}
          />
        )}
      </AdjustedTable>
    );
  };

  override render = (): JSX.Element => {
    const HORIZONTAL_SCROLLBAR_HEIGHT = 15;
    const rowHeight = this.getRowHeight(0);
    const hasFixedColumn = this.props.columns.some((column) => column.isFixed);
    return (
      <div
        className={classNames('scrollableTable', this.props.className, {
          'scrollableTable--compact': this.props.isCompact,
          'scrollableTable--overflow': this.props.allowOverflow,
          'scrollableTable--resizableColumns': this.props.canResizeColumns,
        })}
        {...getTestProps({component: 'ScrollableTable', element: 'root'})}
      >
        <AutoSizer disableHeight={!this.props.matchParentHeight} onResize={this.props.onResize}>
          {({height, width}) => {
            const tableHeight = this.props.matchParentHeight
              ? height
              : rowHeight * (this.props.rows.length || 1) +
                TABLE_HEADER_HEIGHT +
                2 +
                HORIZONTAL_SCROLLBAR_HEIGHT;
            const leftPadding = this.props.calcLeftPadding
              ? this.props.calcLeftPadding(width)
              : TABLE_DEFAULT_LEFT_PADDING;

            if (this.props.rows.length === 0) {
              return (
                <EmptyScrollableTable
                  height={tableHeight}
                  leftPadding={leftPadding}
                  rowHeight={rowHeight}
                  text={this.props.emptyTableText}
                  width={width}
                />
              );
            }

            if (this.props.fixedRowCount > 0) {
              const topTableHeight =
                this.getSumRowHeight(0, this.props.fixedRowCount) + TABLE_HEADER_HEIGHT + 2;
              return (
                <>
                  {this.renderTable(
                    topTableHeight,
                    width,
                    rowHeight,
                    leftPadding,
                    hasFixedColumn,
                    TABLE_HEADER_HEIGHT,
                    TableType.FIXED_ROWS_TOP
                  )}
                  {this.renderTable(
                    tableHeight - topTableHeight,
                    width,
                    rowHeight,
                    leftPadding,
                    hasFixedColumn,
                    0,
                    TableType.FIXED_ROWS_BOTTOM
                  )}
                </>
              );
            } else {
              return this.renderTable(
                tableHeight,
                width,
                rowHeight,
                leftPadding,
                hasFixedColumn,
                TABLE_HEADER_HEIGHT,
                TableType.NO_FIXED_ROWS
              );
            }
          }}
        </AutoSizer>
      </div>
    );
  };
}

type OnRowEvent = (e: React.SyntheticEvent<Table>, rowIndex: number) => void;

/**
 *  Return a hook that adjusts the row index of an event according to table type
 */
function useUseAdjustRowEvent<T extends OnRowEvent | undefined>(
  tableType: TableType,
  fixedRowCount: number
): [(callback: T) => T, (rowIndex: number) => number] {
  const adjustRowCount = useCallback(
    (rowIndex: number) => rowIndex + (tableType === TableType.FIXED_ROWS_BOTTOM ? fixedRowCount : 0),
    [tableType, fixedRowCount]
  );
  const useAdjustRowEvent = (callback: T): T =>
    useMemo(
      () =>
        callback
          ? (((e: React.SyntheticEvent<Table>, rowIndex: number) => {
              callback(e, adjustRowCount(rowIndex));
            }) as T)
          : (undefined as T),
      [callback]
    );

  return [useAdjustRowEvent, adjustRowCount];
}

function AdjustedTable({
  tableType,
  fixedRowCount,
  getRowHeight,
  getRowClassName,
  tableRef,
  ...tableProps
}: TableProps & {
  tableType: TableType;
  fixedRowCount: number;
  getRowHeight: (rowIndex: number) => number;
  getRowClassName: (rowIndex: number) => string;
  tableRef?: React.RefObject<Table>;
}): JSX.Element {
  const [useAdjustRowEvent, adjustRowCount] = useUseAdjustRowEvent(tableType, fixedRowCount);
  const onRowMouseEnter = useAdjustRowEvent(tableProps.onRowMouseEnter);
  const onRowMouseLeave = useAdjustRowEvent(tableProps.onRowMouseLeave);
  const onRowClick = useAdjustRowEvent(tableProps.onRowClick);
  const rowHeightGetter = useCallback(
    (rowIndex: number): number => getRowHeight(adjustRowCount(rowIndex)),
    [getRowHeight, adjustRowCount]
  );
  const classNameGetter = useCallback(
    (rowIndex: number): string => getRowClassName(adjustRowCount(rowIndex)),
    [getRowClassName, adjustRowCount]
  );
  return (
    <Table
      {...tableProps}
      ref={tableRef}
      rowHeightGetter={rowHeightGetter}
      rowClassNameGetter={classNameGetter}
      onRowMouseEnter={onRowMouseEnter}
      onRowMouseLeave={onRowMouseLeave}
      onRowClick={onRowClick}
      className={css(styles.table)}
      onHorizontalScroll={tableProps.onHorizontalScroll}
    />
  );
}

export default ScrollableTableConnector.connect(ScrollableTable);

const styles = StyleSheet.create({
  /* The table height is set by default to 2px larger than its content, which causes
   * a small gap at the bottom of the table. This normally isn't a problem, but with two tables
   * stacked on top of each other, there awkward gap becomes more noticeable. This removes the gap.
   * The default is set using an inline style in the library, so this overrides it.
   */
  table: {
    height: 'initial',
  },
});
